import FilterSelect from '@/components/inputs/FilterSelect';
import Input from '@/components/inputs/Input';
import PortalInputController from '@/components/inputs/PortalInputController';
import { Fragment, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';

import { Option } from '@/constants/general';

import { PortalWhitelistRequestFormValues } from '@/components/form/schema/portalWhitelistSchema';
import TextArea from '@/components/inputs/TextArea';
import { addSpacingToCharacter } from '@/utils/format';
import useLanguage from '@/utils/hooks/useLanguage';
import { useSso } from '@/utils/providers/SSOProvider';

interface RequestBankInputsProps {}

const RequestBankInputs = {
    Section1: (props: RequestBankInputsProps) => {
        const lang = useLanguage();

        const { currenciesApi } = useSso();

        const { watch, trigger, setValue } = useFormContext<PortalWhitelistRequestFormValues>();

        const [selectedBaseCurrency] = watch(['baseCurrencyId']);

        const { data: currencies, isLoading: isLoadingCurrencies } = currenciesApi;

        const currencyOptions: Option[] = useMemo(() => {
            if (!currencies || !currencies || isLoadingCurrencies) {
                return [];
            }

            return (
                currencies &&
                currencies
                    .filter(
                        (currency) =>
                            currency.enabledForDeposit && currency.enabledForWithdrawal && currency.type === 'FIAT'
                    )
                    .map((currency) => {
                        return {
                            label: currency.digitalAssetName || currency.name,
                            value: currency.id
                        };
                    })
            );
        }, [currencies, isLoadingCurrencies]);

        const swiftCodeLabel = useMemo(() => {
            return selectedBaseCurrency?.label === 'USD' ? lang.portal.swiftBICABARoutingNumber : lang.portal.swiftBIC;
        }, [lang, selectedBaseCurrency]);

        return (
            <Fragment>
                <fieldset className="space-y-3">
                    <legend className="text-xs text-brand-neutral-500">{lang.portal.generalInformation}</legend>
                    <PortalInputController
                        name="friendlyName"
                        label={lang.portal.friendlyName}
                        placeholder={lang.portal.friendlyName}
                        required>
                        <Input />
                    </PortalInputController>
                    <PortalInputController
                        name="baseCurrencyId"
                        label={lang.lite.currency}
                        placeholder={lang.lite.currency}
                        asyncInput={isLoadingCurrencies}
                        required>
                        <FilterSelect options={currencyOptions} enableCurrencyIcon enableSorting />
                    </PortalInputController>
                </fieldset>
                <fieldset className="space-y-3 mt-3">
                    <legend className="text-xs text-brand-neutral-500">{lang.portal.bankAccountInformation}</legend>
                    <div className="flex flex-col xsm:flex-row gap-4 w-full">
                        <div className="flex-1">
                            <PortalInputController
                                name="bankName"
                                label={lang.portal.bankName}
                                placeholder={lang.portal.bankName}
                                required>
                                <Input />
                            </PortalInputController>
                        </div>
                        <div className="flex-1">
                            <PortalInputController
                                name="bankAddress"
                                label={lang.portal.bankAddress}
                                placeholder={lang.portal.bankAddress}
                                required>
                                <Input />
                            </PortalInputController>
                        </div>
                    </div>
                </fieldset>
                <fieldset>
                    <PortalInputController
                        name="swiftCode"
                        label={swiftCodeLabel}
                        placeholder={swiftCodeLabel}
                        required>
                        <Input />
                    </PortalInputController>
                </fieldset>
                <fieldset className="space-y-3">
                    <div className="flex flex-col xsm:flex-row gap-4 w-full">
                        <div className="flex-1">
                            <PortalInputController
                                name="ibanCode"
                                label="IBAN"
                                placeholder="IBAN"
                                onChange={(e) => {
                                    setValue('ibanCode', addSpacingToCharacter(e.target.value, 4), {
                                        shouldDirty: true,
                                        shouldValidate: true
                                    });
                                    trigger('accountNumber');
                                }}>
                                <Input />
                            </PortalInputController>
                        </div>
                        <div className="flex-1">
                            <PortalInputController
                                name="accountNumber"
                                label={lang.portal.accountNumber}
                                placeholder={lang.portal.accountNumber}
                                onChange={(e) => {
                                    setValue('accountNumber', e.target.value, {
                                        shouldDirty: true,
                                        shouldValidate: true
                                    });
                                    trigger('ibanCode');
                                }}>
                                <Input />
                            </PortalInputController>
                        </div>
                    </div>
                </fieldset>
                <fieldset>
                    <PortalInputController
                        name="requestDescription"
                        label={lang.portal.requestDescription}
                        placeholder={lang.portal.requestDescription}>
                        <TextArea rows={2} />
                    </PortalInputController>
                </fieldset>
            </Fragment>
        );
    },
    Section2: (props: RequestBankInputsProps) => {
        const lang = useLanguage();
        return (
            <Fragment>
                <fieldset className="space-y-3">
                    <legend className="text-xs text-brand-neutral-500">{lang.portal.correspondentInformation}</legend>
                    <div className="flex flex-col xsm:flex-row gap-4 w-full">
                        <div className="flex-1">
                            <PortalInputController
                                name="correspondentBankName"
                                label={lang.portal.bankName}
                                placeholder={lang.portal.bankName}>
                                <Input />
                            </PortalInputController>
                        </div>
                        <div className="flex-1">
                            <PortalInputController
                                name="correspondentBankSwiftCode"
                                label={lang.portal.swiftCode}
                                placeholder={lang.portal.swiftCode}>
                                <Input />
                            </PortalInputController>
                        </div>
                    </div>
                </fieldset>

                <fieldset className="space-y-3 mt-3 w-full">
                    <legend className="text-xs text-brand-neutral-500">{lang.portal.intermediaryInformation}</legend>
                    <div className="flex flex-col xsm:flex-row gap-4 w-full">
                        <div className="flex-1">
                            <PortalInputController
                                name="intermediaryBankName"
                                label={lang.portal.bankName}
                                placeholder={lang.portal.bankName}>
                                <Input />
                            </PortalInputController>
                        </div>
                        <div className="flex-1">
                            <PortalInputController
                                name="intermediaryBankSwiftCode"
                                label={lang.portal.swiftCode}
                                placeholder={lang.portal.swiftCode}>
                                <Input />
                            </PortalInputController>
                        </div>
                    </div>
                </fieldset>
            </Fragment>
        );
    }
};

export default RequestBankInputs;
